// Follow one intent: wait for the indexer to see the open, then the api's SSE stream
// (`GET /intents/{hash}/stream`: snapshot, update, event, done). EventSource reconnects by itself
// and resends Last-Event-ID; if the stream keeps failing, poll `GET /intents/{hash}` instead. The
// stream ends at `refunded`, or `settled` (and, for a gum_deposit, credited on Jupnet); an api that
// ends it at `settled` regardless has a settled-but-uncredited gum_deposit polled until the credit.

import { getIntent, type IntentEvent, type IntentQuote, type IntentRow } from "./api";
import { isGumDeposit } from "./progress";

export type TrackState = { row: IntentRow; events: IntentEvent[]; quote: IntentQuote | null; via: "stream" | "poll" };
export type TrackHandlers = {
  onState: (s: TrackState) => void;
  /** a transient note: waiting for the indexer, reconnecting, fell back to polling */
  onNote?: (msg: string | null) => void;
  onError?: (msg: string) => void;
};

const POLL_MS = 2000;
const MAX_STREAM_ERRORS = 4;
/** Nothing more will change for the user: refunded, or settled (and credited, for a gum_deposit). */
export const isFinal = (r: IntentRow) => r.state === "refunded" || (r.state === "settled" && (!isGumDeposit(r) || r.credit_tx != null));

export function trackIntent(api: string, hash: string, h: TrackHandlers): () => void {
  let stopped = false;
  let es: EventSource | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let row: IntentRow | null = null;
  let events: IntentEvent[] = [];
  let quote: IntentQuote | null = null;

  const stop = () => {
    stopped = true;
    es?.close();
    if (timer) clearTimeout(timer);
  };
  const emit = (via: TrackState["via"]) => row && h.onState({ row, events, quote, via });

  const poll = async () => {
    if (stopped) return;
    try {
      const r = await getIntent(api, hash);
      if (r) {
        row = r.intent;
        events = r.events;
        quote = r.quote ?? quote;
        emit("poll");
        if (isFinal(row)) return stop();
      }
    } catch (e) {
      h.onNote?.(`polling: ${(e as Error).message}`);
    }
    timer = setTimeout(poll, POLL_MS);
  };

  const stream = () => {
    if (stopped) return;
    let errors = 0;
    es = new EventSource(`${api}/intents/${hash}/stream`);
    es.addEventListener("snapshot", (e) => {
      const s = JSON.parse((e as MessageEvent).data);
      row = s.intent;
      events = s.events;
      quote = s.quote ?? quote;
      errors = 0;
      h.onNote?.(null);
      emit("stream");
    });
    es.addEventListener("update", (e) => {
      row = JSON.parse((e as MessageEvent).data);
      errors = 0;
      emit("stream");
    });
    es.addEventListener("event", (e) => {
      events = [...events, JSON.parse((e as MessageEvent).data)];
      emit("stream");
    });
    es.addEventListener("done", () => {
      if (row && !isFinal(row)) {
        es?.close();
        poll(); // settled, the Jupnet credit still to come
      } else stop();
    });
    es.onerror = () => {
      if (stopped) return;
      errors++;
      if (es?.readyState === EventSource.CLOSED || errors >= MAX_STREAM_ERRORS) {
        es?.close();
        h.onNote?.("live stream unavailable; polling for status");
        poll();
      } else h.onNote?.("reconnecting to the live stream…");
    };
  };

  // the stream answers 404 until the indexer has seen the open: wait for the row first
  const waitIndexed = async (tries = 0) => {
    if (stopped) return;
    try {
      const r = await getIntent(api, hash);
      if (r) {
        row = r.intent;
        events = r.events;
        quote = r.quote ?? quote;
        emit("poll");
        if (isFinal(row)) return stop();
        return stream();
      }
      h.onNote?.("waiting for the indexer to see the open…");
    } catch (e) {
      h.onNote?.(`status: ${(e as Error).message}`);
    }
    if (tries > 600) return h.onError?.("the api never saw this intent; check the hash and the source chain explorer");
    timer = setTimeout(() => waitIndexed(tries + 1), POLL_MS);
  };
  waitIndexed();
  return stop;
}
